import {
  FilterFn,
  getExpandedRowModel,
  Row,
  TableOptions,
} from '@tanstack/react-table';
import { get } from 'lodash';

import { useLockObject } from '../../../hooks/object-lock-subscription/LockObjectProvider';

import { getCustomCoreRowModel } from './get-custom-core-row-model';
import { getManualGroupedRowModel } from './get-manual-grouped-row-model';

declare module '@tanstack/react-table' {
  interface FilterFns {
    closedStatus: true;
  }
}

// The table defaults to numberRange filter function on ClosedStatus columns,
// which breaks the filters by auto-removing them when applied. Set to a custom
// filter function and configure auto-remove.
const closedStatusFilterFn: FilterFn<unknown> = () => true;
closedStatusFilterFn.autoRemove = (value) => {
  return value == null || value.value === false;
};

export function useDefaultReactTableOptions<T = unknown>(): Pick<
  TableOptions<T>,
  | 'getCoreRowModel'
  | 'getGroupedRowModel'
  | 'getExpandedRowModel'
  | 'autoResetExpanded'
  | 'getRowId'
  | 'getSubRows'
  | 'enableSortingRemoval'
  | 'columnResizeMode'
  | 'filterFns'
  | 'getLock'
> {
  const { getLock } = useLockObject();

  return {
    getCoreRowModel: getCustomCoreRowModel(),
    getGroupedRowModel: getManualGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
    getRowId,
    getSubRows: getSubRows,
    enableSortingRemoval: false,
    columnResizeMode: 'onChange',
    filterFns: {
      closedStatus: closedStatusFilterFn,
    },
    getLock: getLock,
  };
}

const getSubRows = (value: any) => get(value, 'subRows');

function getRowId(row: any, relativeIndex: number, parent: Row<any>): string {
  const id: string | undefined = row?.id || row?.primaryKey;
  const groupByVal: string | undefined = row?.meta?.groupByVal;

  const ownId = String((id ?? groupByVal) || `r${relativeIndex}`);
  const fullId = parent ? [parent.id, ownId].join('.') : ownId;

  return fullId;
}
